import {
  InspectionDrawer,
  SiteDetailDrawer,
  SiteLogDrawer,
} from '@/components/drawers';
import Header from '@/components/layouts/Header';
import { MotorControlModal } from '@/components/modals';
import { useDrawerState } from '@/features/site-information/hooks/useDrawerState';
import { usePagination } from '@/features/site-information/hooks/usePagination';
import { useSiteData } from '@/features/site-information/hooks/useSiteData';
import { useSorting } from '@/features/site-information/hooks/useSorting';
import { useBatchSiteComprehensiveStatus, useDrawerRestore } from '@/hooks';
import type { SiteData } from '@/types/site';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import SitePaginationPanel from './SitePaginationPanel';
import SiteSearchBar from './SiteSearchBar';
import SiteStatsCards from './SiteStatsCards';
import SiteTable from './SiteTable';
import SiteTableEmpty from './SiteTableEmpty';
import SiteTableError from './SiteTableError';
import SiteTableHeader from './SiteTableHeader';
import SiteTableSkeleton from './SiteTableSkeleton';

export default function SiteInformation() {
  const queryClient = useQueryClient();

  // 커스텀 훅들로 로직 분리
  const {
    searchTerm,
    setSearchTerm,
    filteredSites,
    refreshData,
    isLoading,
    error,
    stats,
    totalSites,
  } = useSiteData();

  const handleRefresh = () => {
    queryClient.invalidateQueries();
  };

  // 1. 먼저 필터링과 정렬을 수행 (comprehensive status 없이)
  const { sortedSites, handleSort, renderSortIcon } = useSorting(filteredSites);

  // 2. 정렬된 데이터를 페이지네이션
  const {
    currentPage,
    itemsPerPage,
    totalPages,
    startIndex,
    endIndex,
    currentItems: currentSites,
    totalItems,
    handlePageChange,
    handleItemsPerPageChange,
  } = usePagination<SiteData>(sortedSites, searchTerm);

  // 3. 현재 페이지에 표시되는 사이트들의 comprehensive status만 가져오기
  const currentSiteIds = currentSites.map((site) => site.siteId);
  const {
    data: comprehensiveStatusData,
    isLoading: isComprehensiveStatusLoading,
  } = useBatchSiteComprehensiveStatus(currentSiteIds, {
    enabled: currentSiteIds.length > 0 && !isLoading,
    refetchInterval: 1 * 60 * 1000, // 1분마다 자동 갱신
  });

  const {
    selectedSite,
    activeDrawer,
    drawerExpanded,
    setDrawerExpanded,
    handleSiteClick,
    closeDrawer,
    inspectionDrawerOpen,
    selectedSiteForInspection,
    handleInspectionClick,
    closeInspectionDrawer,
    detailDrawerOpen,
    selectedSiteForDetail,
    handleDetailClick,
    closeDetailDrawer,
  } = useDrawerState();

  // 모터 제어 모달 상태
  const [controlModalOpen, setControlModalOpen] = useState(false);
  const [selectedSiteForControl, setSelectedSiteForControl] =
    useState<SiteData | null>(null);

  const handleControlClick = (site: SiteData, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedSiteForControl(site);
    setControlModalOpen(true);
  };

  const closeControlModal = () => {
    setControlModalOpen(false);
    setSelectedSiteForControl(null);
  };

  // 새로고침 후 drawer 상태 복원
  useDrawerRestore(filteredSites);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main
        className={`transition-all duration-300 ${(activeDrawer === 'site' && !drawerExpanded) || activeDrawer === 'inspection' || activeDrawer === 'detail' ? 'mr-[33vw]' : ''} w-full px-4 sm:px-6 lg:px-8 py-8`}
      >
        {/* 페이지 헤더 */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                현장 정보
              </h1>
              <p className="text-gray-600">
                모든 물 관리 현장을 모니터링하고 관리합니다
              </p>
            </div>
          </div>
        </div>

        {/* 검색 및 필터 영역 */}
        <SiteSearchBar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          onRefresh={refreshData}
          resultsCount={filteredSites.length}
          isLoading={isLoading}
          totalSites={totalSites}
        />

        {/* 테이블 영역 */}
        {isLoading ? (
          <SiteTableSkeleton />
        ) : error ? (
          <SiteTableError error={error} onRetry={handleRefresh} />
        ) : filteredSites.length === 0 ? (
          <SiteTableEmpty
            hasSearchTerm={!!searchTerm}
            searchTerm={searchTerm}
            onClearSearch={() => setSearchTerm('')}
          />
        ) : (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b flex items-center justify-between sm:flex-row flex-col">
              <h2 className="text-xl font-semibold text-gray-900">
                현장 목록{` `}
                {searchTerm && (
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    ({filteredSites.length}개 결과)
                  </span>
                )}
                {!searchTerm && (
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    (총 {totalSites}개)
                  </span>
                )}
              </h2>
              <SiteTableHeader
                itemsPerPage={itemsPerPage}
                totalItems={totalItems}
                totalPages={totalPages}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>

            <SiteTable
              sites={currentSites}
              comprehensiveStatusData={comprehensiveStatusData}
              isComprehensiveStatusLoading={isComprehensiveStatusLoading}
              onSiteClick={handleSiteClick}
              onInspectionClick={handleInspectionClick}
              onDetailClick={handleDetailClick}
              onControlClick={handleControlClick}
              onSort={handleSort}
              renderSortIcon={renderSortIcon}
              searchTerm={searchTerm}
              filteredCount={filteredSites.length}
            />

            <SitePaginationPanel
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              startIndex={startIndex}
              endIndex={endIndex}
              onPageChange={handlePageChange}
            />
          </div>
        )}

        {/* 통계 카드 */}
        <SiteStatsCards
          sites={filteredSites}
          comprehensiveStatusData={comprehensiveStatusData}
          stats={stats}
          isLoading={isLoading}
        />
      </main>

      <SiteDetailDrawer
        selectedSite={selectedSite}
        drawerExpanded={drawerExpanded}
        setDrawerExpanded={setDrawerExpanded}
        onClose={closeDrawer}
      />
      <InspectionDrawer
        isOpen={inspectionDrawerOpen}
        selectedSite={selectedSiteForInspection}
        onClose={closeInspectionDrawer}
      />
      <SiteLogDrawer
        isOpen={detailDrawerOpen}
        selectedSite={selectedSiteForDetail}
        onClose={closeDetailDrawer}
      />
      <MotorControlModal
        isOpen={controlModalOpen}
        site={selectedSiteForControl}
        onClose={closeControlModal}
      />
    </div>
  );
}
